import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    MenuItem,
    Stack,
    TextField,
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { hashString } from '../Helpers/hashString';
import { Person } from '../Models/person';
import { SessionWithId } from '../Models/session';
import { openAlert } from '../Store/AlertSlice';
import { addAddress } from '../firebase/Database/AddAddress';
import { addSession } from '../firebase/Database/AddSession';
import { addTag } from '../firebase/Database/AddTag';
import { editSession } from '../firebase/Database/EditSession';
import { getAddresses } from '../firebase/Database/GetAddresses';
import { getAllPeople } from '../firebase/Database/GetAllPeople';
import { getTags } from '../firebase/Database/GetTags';

interface EditSessionDialogProps {
    open: boolean;
    close: () => void;
    session?: SessionWithId;
    edit: boolean;
}

export const AddEditSessionDialog: React.FC<EditSessionDialogProps> = ({
    open,
    close,
    session,
    edit,
}) => {
    const [dateValue, setDateValue] = useState<Dayjs | null>(
        session
            ? dayjs()
                  .set('y', session.session.year)
                  .set('M', session.session.month - 1)
                  .set('D', session.session.day)
                  .set('h', session.session.hour)
                  .set('m', session.session.minute)
            : dayjs().startOf('day'),
    );
    const [capacity, setCapacity] = useState<number>(
        session ? session.session.capacity : 6,
    );
    const [lessonCount, setLessonCount] = useState<number>(1);
    const [users, setUsers] = useState<Person[] | null>(null);
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const [description, setDescription] = useState<string>(
        session ? session.session.description : '',
    );
    const [online, setOnline] = useState<boolean>(
        session ? session.session.online : false,
    );
    const [special, setSpecial] = useState<boolean>(
        session && session.session.special ? session.session.special : false,
    );
    const [location, setLocation] = useState<string>(
        session ? session.session.location : '',
    );
    const [difficulty, setDifficulty] = useState<string>(
        session ? session.session.difficulty : '',
    );
    const [difficulties, setDifficulties] = useState<string[]>([]);
    const [newTag, setNewTag] = useState<boolean>(false);
    const [newTagText, setNewTagText] = useState<string>('');
    const [addresses, setAddresses] = useState<string[]>([]);
    const [newAddress, openNewAddress] = useState<boolean>(false);
    const [newAddressText, setNewAddressText] = useState<string>('');
    const dispatch = useDispatch();

    const addNewAddress = async () => {
        addAddress(newAddressText)
            .then(() => {
                dispatch(
                    openAlert({
                        displayText: 'Adresa byla úspěšně přidána',
                        severity: 'success',
                    }),
                );

                setAddresses([...addresses, newAddressText]);
            })
            .catch((err) => {
                dispatch(
                    openAlert({
                        displayText: 'Adresu se nepodařilo přidat',
                        severity: 'error',
                    }),
                );
            });

        setNewAddressText('');
        openNewAddress(false);
    };

    const addNewTag = async () => {
        addTag(newTagText)
            .then(() => {
                dispatch(
                    openAlert({
                        displayText: 'Tag byl úspěšně přidán',
                        severity: 'success',
                    }),
                );

                setAddresses([...addresses, newAddressText]);
            })
            .catch((err) => {
                dispatch(
                    openAlert({
                        displayText: 'Tag se nepodařilo přidat',
                        severity: 'error',
                    }),
                );
            });

        setNewTagText('');
        setNewTag(false);
    };

    useEffect(() => {
        if (session) {
            setDateValue(
                dayjs()
                    .set('y', session.session.year)
                    .set('M', session.session.month - 1)
                    .set('D', session.session.day)
                    .set('h', session.session.hour)
                    .set('m', session.session.minute),
            );
            setCapacity(session.session.capacity);
            setLessonCount(1);
            setDescription(session.session.description);
            setDifficulty(session.session.difficulty);
            setOnline(session.session.online);
            setLocation(session.session.location);
        }
    }, [open, session]);

    useEffect(() => {
        getAllPeople()
            .then((res) => {
                setUsers(res);
            })
            .catch((err) => {
                dispatch(
                    openAlert({
                        displayText: 'Problém během načítání lidí',
                        severity: 'error',
                    }),
                );
            });

        getAddresses()
            .then((res) => {
                const addresses = res.map((address) => address.address);
                setAddresses(addresses);
            })
            .catch((err) => {
                dispatch(
                    openAlert({
                        displayText: 'Problém během načítání adres',
                        severity: 'error',
                    }),
                );
            });

        getTags()
            .then((res) => {
                console.log('Tags: ', res);
                const tags = res.map((tag) => tag.tag);
                setDifficulties(tags);
            })
            .catch((err) => {
                dispatch(
                    openAlert({
                        displayText: 'Problém během načítání tagů',
                        severity: 'error',
                    }),
                );
            });
    }, [dispatch]);

    const onEdit = () => {
        if (session) {
            const year = dateValue!.year();
            const month = dateValue!.month() + 1;
            const day = dateValue!.date();
            const hour = dateValue!.hour();
            const minute = dateValue!.minute();

            editSession(
                session.id,
                year,
                month,
                day,
                hour,
                minute,
                capacity,
                description,
                difficulty,
                online,
                location,
            )
                .then(() => {
                    dispatch(
                        openAlert({
                            displayText: 'Lekce byla úspěšně upravena',
                            severity: 'success',
                        }),
                    );
                })
                .catch((err) => {
                    dispatch(
                        openAlert({
                            displayText: 'Nepodařilo se upravit lekci!',
                            severity: 'error',
                        }),
                    );
                });

            onCloseHandler();
        }
    };

    const onAdd = () => {
        if (dateValue == null) {
            dispatch(
                openAlert({
                    displayText: 'Datum musí být vyplněno',
                    severity: 'error',
                }),
            );
            return;
        }

        const selectedUsersFormatted: { [personId: string]: boolean } =
            selectedUsers.reduce(
                (result, user) => {
                    result[user] = true;
                    return result;
                },
                {} as { [personId: string]: boolean },
            );

        for (let i = 0; i < lessonCount; i++) {
            const newDateValue = dateValue.add(i, 'week');
            const year = newDateValue.year();
            const month = newDateValue.month() + 1;
            const day = newDateValue.date();
            const hour = newDateValue.hour();
            const minute = newDateValue.minute();

            addSession(
                year,
                month,
                day,
                hour,
                minute,
                capacity,
                description,
                difficulty,
                online,
                location,
                selectedUsersFormatted,
                special,
            )
                .then(() => {
                    dispatch(
                        openAlert({
                            displayText: 'Lekce byla úspěšně vytvořena',
                            severity: 'success',
                        }),
                    );
                })
                .catch((err) => {
                    dispatch(
                        openAlert({
                            displayText: 'Lekci nebylo možné vytvořit',
                            severity: 'error',
                        }),
                    );
                });
        }

        onCloseHandler();
    };

    const onCloseHandler = () => {
        setCapacity(6);
        setDateValue(dayjs().startOf('day'));
        setLessonCount(1);
        setSelectedUsers([]);
        setDescription('');
        setOnline(false);
        setSpecial(false);
        setLocation('');
        setDifficulty('');
        close();
    };

    return (
        <Dialog open={open} onClose={close}>
            <DialogTitle>
                {edit ? 'Úprava lekce' : 'Vytvoření nové lekce'}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ marginTop: '10px' }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                            label="Datum"
                            format="DD.MM.YYYY HH:mm"
                            value={dateValue}
                            onChange={(newValue) => setDateValue(newValue)}
                            ampm={false}
                        />
                    </LocalizationProvider>
                    <TextField
                        type="number"
                        label="Kapacita"
                        value={capacity}
                        onChange={(newValue) =>
                            setCapacity(Number(newValue.currentTarget.value))
                        }
                    />
                    <TextField
                        type="text"
                        label="Popis"
                        value={description}
                        onChange={(newValue) =>
                            setDescription(newValue.currentTarget.value)
                        }
                    />

                    {!edit && (
                        <TextField
                            type="number"
                            label="Vytvořit pro n týdnů"
                            value={lessonCount}
                            onChange={(newValue) =>
                                setLessonCount(
                                    Number(newValue.currentTarget.value),
                                )
                            }
                        />
                    )}
                    <TextField
                        select
                        label="Obtížnost"
                        variant="outlined"
                        value={difficulty}
                        onChange={(newValue) =>
                            setDifficulty(newValue.target.value)
                        }
                        fullWidth
                    >
                        {difficulties.map((difficultyValue) => (
                            <MenuItem
                                key={difficultyValue}
                                value={difficultyValue}
                            >
                                {difficultyValue}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => {
                            setNewTag((prevValue) => !prevValue);
                        }}
                    >
                        Přidat nový tag
                    </Button>
                    {newTag && (
                        <>
                            <Grid item xs={12}>
                                <TextField
                                    type="text"
                                    label="Nový tag"
                                    value={newTagText}
                                    onChange={(newValue) => {
                                        setNewTagText(
                                            newValue.currentTarget.value,
                                        );
                                    }}
                                ></TextField>
                            </Grid>
                            <Grid item xs={4}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={addNewTag}
                                >
                                    Přidat
                                </Button>
                            </Grid>
                        </>
                    )}
                    <FormControlLabel
                        control={
                            <Checkbox checked={online} color="secondary" />
                        }
                        label="Online"
                        onChange={(
                            event: React.SyntheticEvent<Element, Event>,
                            checked: boolean,
                        ) => setOnline(checked)}
                    />
                    {users && !edit && (
                        <TextField
                            select
                            label="Přidat uživatele"
                            variant="outlined"
                            SelectProps={{
                                multiple: true,
                                value: selectedUsers,
                                onChange: (ev) => {
                                    setSelectedUsers(
                                        ev.target.value as string[],
                                    );
                                },
                            }}
                            sx={{ margin: '10px 0' }}
                        >
                            {users.map((user, index) => {
                                return (
                                    <MenuItem
                                        key={index}
                                        value={hashString(user.email)}
                                    >
                                        {user.firstName} {user.lastName}
                                    </MenuItem>
                                );
                            })}
                        </TextField>
                    )}
                    <TextField
                        select
                        label="Adresa"
                        value={location}
                        variant="outlined"
                        onChange={(newValue) =>
                            setLocation(newValue.target.value)
                        }
                    >
                        {addresses.map((address) => (
                            <MenuItem key={address} value={address}>
                                {address}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Grid container>
                        <Grid item xs={10}>
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => {
                                    openNewAddress(true);
                                }}
                            >
                                Přidat novou adresu
                            </Button>
                        </Grid>
                    </Grid>
                    {newAddress && (
                        <>
                            <Grid item xs={12}>
                                <TextField
                                    type="text"
                                    label="Nová adresa"
                                    value={newAddressText}
                                    onChange={(newValue) => {
                                        setNewAddressText(
                                            newValue.currentTarget.value,
                                        );
                                    }}
                                ></TextField>
                            </Grid>
                            <Grid item xs={4}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={addNewAddress}
                                >
                                    Přidat
                                </Button>
                            </Grid>
                        </>
                    )}

                    <FormControlLabel
                        control={
                            <Checkbox checked={special} color="secondary" />
                        }
                        label="Speciální"
                        onChange={(
                            event: React.SyntheticEvent<Element, Event>,
                            checked: boolean,
                        ) => setSpecial(checked)}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button color="secondary" onClick={edit ? onEdit : onAdd}>
                    Potvrdit
                </Button>
                <Button color="secondary" onClick={close}>
                    Zrušit
                </Button>
            </DialogActions>
        </Dialog>
    );
};
